import { useEffect, useState } from 'react';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Box, Collapse, Typography, useTheme } from '@mui/material';
import PropTypes from 'prop-types';
import { CONCEPT_BUILDER_KEY, PRACTICE_TEST_KEY } from '../../../../config/navigation/navigation.constants';
import { getMenuIcon } from '../../../../config/navigation/menuIcons';
import { isAnyChildRouteActive, isRouteActive } from '../../../../utils/navigation/resolveActiveRoute';
import useAppShellSx from '../../hooks/useAppShellSx';
import useSidebarTransition from '../../hooks/useSidebarTransition';
import SidebarNavItem from './SidebarNavItem';
import SidebarTooltip from './SidebarTooltip';

function renderNestedItems({
  items,
  collapsed,
  depth,
  pathname,
  onNavigate,
  expandMap,
  toggleExpand,
  getNavButtonSx,
  sidebarType,
}) {
  return items.map((child) => {
    if (!child) return null;

    const childKey = child.key || child.label;
    const hasChildren = Array.isArray(child.children) && child.children.length > 0;
    const childRouteActive = isAnyChildRouteActive(pathname, child.children);
    // Expand-only rows never take the active pill — only real routes do (same as leaf items).
    const active = child.route
      ? isRouteActive(pathname, child.route) || childRouteActive
      : false;

    if (hasChildren) {
      const expanded = expandMap[childKey] || childRouteActive;
      return (
        <Box key={childKey}>
          <Box
            component="button"
            type="button"
            onClick={() => toggleExpand(childKey)}
            sx={{
              width: '100%',
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              px: 1.5 + depth * 1.25,
              py: 0.75,
              mx: 1,
              ...getNavButtonSx({ active, disabled: false, collapsed: false }),
            }}
          >
            <Typography
              component="span"
              sx={{ flex: 1, textAlign: 'left', ...sidebarType.navLabelNested(active) }}
            >
              {child.label || child.title}
            </Typography>
            {expanded ? (
              <ExpandLessIcon sx={{ fontSize: 18 }} />
            ) : (
              <ExpandMoreIcon sx={{ fontSize: 18 }} />
            )}
          </Box>
          <Collapse in={expanded} timeout="auto" unmountOnExit>
            {renderNestedItems({
              items: child.children,
              collapsed,
              depth: depth + 1,
              pathname,
              onNavigate,
              expandMap,
              toggleExpand,
              getNavButtonSx,
              sidebarType,
            })}
          </Collapse>
        </Box>
      );
    }

    return (
      <SidebarNavItem
        key={childKey}
        item={child}
        collapsed={collapsed}
        depth={depth}
        active={isRouteActive(pathname, child.route)}
        onNavigate={onNavigate}
      />
    );
  });
}

export default function SidebarNavGroup({
  item,
  collapsed,
  pathname,
  onNavigate,
  onConceptBuilderExpand,
}) {
  const theme = useTheme();
  const { sidebar, sidebarType } = useAppShellSx();
  const { getLabelSx, getNavItemLayoutSx, getRailItemWrapSx, sectionCollapseTimeout, getNavButtonSx, getIconWrapSx, getNavIconSx } =
    useSidebarTransition();
  const navLayout = getNavItemLayoutSx(collapsed, 0);
  const [expanded, setExpanded] = useState(false);
  const [expandMap, setExpandMap] = useState({});

  const IconComponent = getMenuIcon(item.iconKey);
  const hasChildren = Array.isArray(item.children) && item.children.length > 0;
  const childActive = isAnyChildRouteActive(pathname, item.children);
  // Menu groups (Dashboard, WhatsApp, …) only expand/collapse — active pill stays on the leaf.
  const active = item.route
    ? isRouteActive(pathname, item.route) || childActive
    : false;

  useEffect(() => {
    if (childActive) {
      setExpanded(true);
    }
  }, [childActive]);

  const toggleExpand = (key) => {
    setExpandMap((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const handleTopClick = () => {
    if (item.key === CONCEPT_BUILDER_KEY || item.key === PRACTICE_TEST_KEY) {
      onConceptBuilderExpand?.();
    }

    if (item.route) {
      onNavigate(item.route);
      return;
    }

    if (hasChildren) {
      setExpanded((prev) => !prev);
    }
  };

  const row = (
    <Box
      component="button"
      type="button"
      onClick={handleTopClick}
      sx={{
        display: 'flex',
        alignItems: 'center',
        ...getNavButtonSx({ active, disabled: false, collapsed }),
        ...navLayout,
        py: collapsed ? 0 : 0.625,
        my: collapsed ? 0 : 0.125,
        transition: `${navLayout.transition}, ${theme.transitions.create(['background-color', 'color', 'box-shadow'], {
          duration: theme.transitions.duration.shorter,
        })}`,
      }}
    >
      {IconComponent && (
        <Box sx={getIconWrapSx()}>
          <IconComponent sx={getNavIconSx()} />
        </Box>
      )}
      <Box
        sx={{
          ...getLabelSx(collapsed),
          ...(!collapsed && { display: 'flex', alignItems: 'center', gap: 1.5 }),
        }}
      >
        <Typography
          component="span"
          sx={{ flex: 1, textAlign: 'left', ...sidebarType.navLabel(active) }}
        >
          {item.title || item.label}
        </Typography>
        {item.badge != null && (
          <Box
            sx={{
              minWidth: 20,
              height: 20,
              px: 0.625,
              borderRadius: 999,
              bgcolor: sidebar.accent,
              color: sidebar.badgeForeground,
              ...sidebarType.badge,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            {item.badge}
          </Box>
        )}
        {hasChildren &&
          (expanded ? (
            <ExpandLessIcon sx={{ fontSize: 18, flexShrink: 0 }} />
          ) : (
            <ExpandMoreIcon sx={{ fontSize: 18, flexShrink: 0 }} />
          ))}
      </Box>
      {collapsed && item.badge != null && (
        <Box
          sx={{
            position: 'absolute',
            top: 6,
            left: 6,
            width: 7,
            height: 7,
            borderRadius: '50%',
            bgcolor: sidebar.accent,
          }}
        />
      )}
    </Box>
  );

  return (
    <Box>
      {collapsed ? (
        <SidebarTooltip title={item.title || item.label}>
          <Box sx={getRailItemWrapSx()}>{row}</Box>
        </SidebarTooltip>
      ) : (
        row
      )}

      {!collapsed && hasChildren && (
        <Collapse in={expanded} timeout={sectionCollapseTimeout} unmountOnExit>
          <Box sx={{ pb: 0.5 }}>
            {renderNestedItems({
              items: item.children,
              collapsed,
              depth: 1,
              pathname,
              onNavigate,
              expandMap,
              toggleExpand,
              getNavButtonSx,
              sidebarType,
            })}
          </Box>
        </Collapse>
      )}
    </Box>
  );
}

SidebarNavGroup.propTypes = {
  item: PropTypes.object.isRequired,
  collapsed: PropTypes.bool,
  pathname: PropTypes.string.isRequired,
  onNavigate: PropTypes.func.isRequired,
  onConceptBuilderExpand: PropTypes.func,
};
